import type { QueryClient } from '@tanstack/react-query';

import type { WatchList, WatchView } from '~/shared/data-contract/watch';

import { jobArchitectureKeys } from './query-keys';

const createOptimisticWatch = (itemId: string): WatchView => ({
    createdAt: new Date().toISOString(),
    explicit: true,
    id: crypto.randomUUID(),
    implicit: false,
    itemId,
    muted: false,
});

export const optimisticallyWatchItem = (queryClient: QueryClient, itemId: string): WatchList => {
    const previous = queryClient.getQueryData<WatchList>(jobArchitectureKeys.watches()) ?? [];

    queryClient.setQueryData<WatchList>(jobArchitectureKeys.watches(), (watches = []) => {
        const existing = watches.find((watch) => watch.itemId === itemId);

        if (existing !== undefined) {
            return watches.map((watch) => (watch.itemId === itemId ? { ...watch, explicit: true } : watch));
        }

        return [...watches, createOptimisticWatch(itemId)];
    });

    return previous;
};

export const optimisticallyUnwatchItem = (queryClient: QueryClient, itemId: string): WatchList => {
    const previous = queryClient.getQueryData<WatchList>(jobArchitectureKeys.watches()) ?? [];

    queryClient.setQueryData<WatchList>(jobArchitectureKeys.watches(), (watches = []) =>
        watches.flatMap((watch) => {
            if (watch.itemId !== itemId || !watch.explicit) {
                return [watch];
            }

            if (watch.implicit) {
                return [{ ...watch, explicit: false }];
            }

            return [];
        }),
    );

    return previous;
};

export const restoreWatches = (queryClient: QueryClient, previous: WatchList): void => {
    queryClient.setQueryData(jobArchitectureKeys.watches(), previous);
};
